import { use, useEffect, useMemo, useState } from 'react';
import moment, { Moment } from 'moment';
import { jsonParseWithLowerCaseKeys } from 'shared/utils/string-utils';

import AcousticConfiguration from 'features/residents/models/configurations/acoustic-configuration';
import AcousticTimeFrameConfiguration from 'features/residents/models/configurations/acoustic-time-frame-configuration';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';

type Props = {
  index: number;
};

type AcousticScenarioConfiguration = {
  configuration: AcousticConfiguration;
  tempConfigDisabled: boolean;
  notInEditMode: boolean;
  updateResidentSettingEditFormScenario: () => void;
  tempConfigEnabledChanged: (enabled: boolean) => void;
  timeFrameConfigurationsChanged: (newTimeFrameConfigurations: AcousticTimeFrameConfiguration[] | null, newActiveAllDay: boolean) => void;
  tempConfigSoundThresholdChanged: (value: number) => void;
  tempConfigDurationChanged: (value: number) => void;
  temporaryValidUntilTimeChanged: (value: Moment | null) => void;
};

export const useAcousticScenarioConfiguration = ({ index }: Props): AcousticScenarioConfiguration => {
  const {
    detailEditState: { viewingMode, currentResidentSetting, residentSettingEditForm },
  } = use(ResidentSettingsProviderContext);
  const rawConfiguration = useMemo(
    () => currentResidentSetting?.organisationUnitScenarios[index].configuration,
    [currentResidentSetting, index],
  );

  const [configuration, setConfiguration] = useState(jsonParseWithLowerCaseKeys(rawConfiguration));

  const tempConfigDisabled = viewingMode === 'viewing' || configuration?.temporaryConfigurationEnabled;
  const notInEditMode = viewingMode === 'viewing';

  const updateScenarioConfiguration = (newConfiguration: unknown): void => {
    setConfiguration(newConfiguration as never);
  };

  const updateResidentSettingEditFormScenario = (): void => {
    const acousticConfiguration = jsonParseWithLowerCaseKeys(
      residentSettingEditForm.getValues(`organisationUnitScenarios.${index}.configuration`),
    );

    residentSettingEditForm.setValue(`organisationUnitScenarios.${index}.configuration`, JSON.stringify(configuration), {
      shouldDirty: JSON.stringify(acousticConfiguration) !== JSON.stringify(configuration),
    });
  };

  const timeFrameConfigurationsChanged = (
    newTimeFrameConfigurations: AcousticTimeFrameConfiguration[] | null,
    newActiveAllDay: boolean,
  ): void => {
    const newConfig = {
      ...configuration,
      acousticTimeFrameConfigurations: newTimeFrameConfigurations,
      timeFrames: newTimeFrameConfigurations?.map((x) => x.timeFrame) ?? null,
      sendAlarmAllDay: newActiveAllDay,
    };
    updateScenarioConfiguration(newConfig);
    residentSettingEditForm.setValue(`organisationUnitScenarios.${index}.configuration`, JSON.stringify(newConfig), {
      shouldDirty: true,
    });
  };

  const tempConfigEnabledChanged = (enabled: boolean): void => {
    const newConfig = {
      ...configuration,
      temporaryConfigurationEnabled: enabled,
      temporaryConfiguration: enabled
        ? {
            validUntil: moment().startOf('hour').add(1, 'hour').toISOString(),
            duration: 4,
            soundThreshold: 4,
          }
        : null,
    };

    updateScenarioConfiguration(newConfig);
    residentSettingEditForm.setValue(`organisationUnitScenarios.${index}.configuration`, JSON.stringify(newConfig), {
      shouldDirty: true,
    });
  };

  const tempConfigSoundThresholdChanged = (value: number): void => {
    const newConfiguration = {
      ...configuration,
      temporaryConfiguration: {
        ...configuration.temporaryConfiguration,
        soundThreshold: value,
      },
    };
    updateScenarioConfiguration(newConfiguration);
  };

  const tempConfigDurationChanged = (value: number): void => {
    const newConfiguration = {
      ...configuration,
      temporaryConfiguration: {
        ...configuration.temporaryConfiguration,
        duration: value,
      },
    };
    updateScenarioConfiguration(newConfiguration);
  };

  const temporaryValidUntilTimeChanged = (value: Moment | null): void => {
    if (!value) {
      return;
    }

    const newConfiguration = {
      ...configuration,

      temporaryConfiguration: {
        ...configuration.temporaryConfiguration,
        validUntil: value?.isBefore(moment())
          ? moment().add(1, 'day').hour(value.hour()).minute(0).second(0).millisecond(0).toISOString()
          : moment().hour(value.hour()).minute(0).second(0).millisecond(0).toISOString(),
      },
    };
    updateScenarioConfiguration(newConfiguration);
    residentSettingEditForm.setValue(`organisationUnitScenarios.${index}.configuration`, JSON.stringify(newConfiguration), {
      shouldDirty: true,
    });
  };

  useEffect(() => {
    residentSettingEditForm.clearErrors(`organisationUnitScenarios.${index}`);
    setConfiguration(jsonParseWithLowerCaseKeys(rawConfiguration) as AcousticConfiguration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawConfiguration, viewingMode]);

  return {
    configuration,
    tempConfigDisabled,
    notInEditMode,
    updateResidentSettingEditFormScenario,
    tempConfigEnabledChanged,
    timeFrameConfigurationsChanged,
    tempConfigSoundThresholdChanged,
    tempConfigDurationChanged,
    temporaryValidUntilTimeChanged,
  };
};
